import { GameConfig } from "@/config/GameConfig";
import type { LevelDefinition } from "./types";
import { horizontalWall, mergeUnique, verticalWall } from "./geometry";

// Shrunk from the original 21x21 (-3 cols, -3 rows total, not per side) to
// tighten the board: less room to react, and RUSH's extra length/speed
// become a real risk instead of free value. Every level below was
// hand-adjusted to the new bounds (not auto-scaled), keeping each map's
// original shape/identity and re-verified for legality (start/obstacles/
// exit in bounds, fully connected play area, exit reachable) by
// `LevelValidator` - see `LevelValidator.test.ts`.
const GRID = { cols: 18, rows: 18 };

export const WORLD_1_LEVELS: LevelDefinition[] = [
  {
    id: "w1-l1",
    name: "OPEN FIELD",
    worldId: "w1",
    levelInWorld: 1,
    order: 1,
    grid: GRID,
    snakeStart: { x: 6, y: 9 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 8,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w1,
    obstacles: [],
    exit: { x: 16, y: 9 },
    pace: { paceParMs: 64000, pacePointsPerSecond: 2, maxPaceBonus: 40 },
  },
  {
    id: "w1-l2",
    name: "GROWING UP",
    worldId: "w1",
    levelInWorld: 2,
    order: 2,
    grid: GRID,
    snakeStart: { x: 4, y: 4 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 12,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w1,
    obstacles: [],
    exit: { x: 13, y: 13 },
    pace: { paceParMs: 73000, pacePointsPerSecond: 2, maxPaceBonus: 40 },
  },
  {
    id: "w1-l3",
    name: "THE DIVIDE",
    worldId: "w1",
    levelInWorld: 3,
    order: 3,
    grid: GRID,
    snakeStart: { x: 4, y: 9 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 13,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w1,
    // A single central wall column, open at the top and bottom of the
    // board so both sides always stay reachable from one another.
    obstacles: verticalWall(9, 3, 14),
    exit: { x: 15, y: 9 },
    pace: { paceParMs: 76000, pacePointsPerSecond: 2, maxPaceBonus: 40 },
  },
  {
    id: "w1-l4",
    name: "CORRIDORS",
    worldId: "w1",
    levelInWorld: 4,
    order: 4,
    grid: GRID,
    snakeStart: { x: 3, y: 3 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 15,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w1,
    // Two staggered horizontal walls form a wide S-shaped corridor:
    // top band -> (gap on the right) -> middle band -> (gap on the
    // left) -> bottom band. Every gap is 5 cells wide for comfortable
    // swipe control.
    // PR28 WORMHOLE edge-safety fix: each wall originally touched the
    // board's LEFT/RIGHT boundary column at its non-gap end (top band's
    // x=0, bottom band's x=17), so a horizontal WORMHOLE wrap at row 6 or
    // row 11 could land directly inside the wall. Trimmed by exactly the
    // one cell that touched the boundary (top band now starts at x=1, not
    // x=0; bottom band now ends at x=16, not x=17) - the 5-cell gaps on
    // each band's opposite end are completely untouched, so the intended
    // S-shaped forced path is unchanged.
    obstacles: mergeUnique(horizontalWall(6, 1, 12), horizontalWall(11, 5, 16)),
    exit: { x: 9, y: 15 },
    pace: { paceParMs: 92000, pacePointsPerSecond: 2, maxPaceBonus: 40 },
  },
  {
    id: "w1-l5",
    name: "THE U",
    worldId: "w1",
    levelInWorld: 5,
    order: 5,
    grid: GRID,
    snakeStart: { x: 3, y: 3 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 18,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w1,
    // A U-shaped obstacle open at the top, sitting near the center of
    // the board.
    obstacles: mergeUnique(verticalWall(7, 7, 12), verticalWall(10, 7, 12), horizontalWall(12, 8, 9)),
    exit: { x: 15, y: 15 },
    pace: { paceParMs: 85000, pacePointsPerSecond: 2, maxPaceBonus: 46 },
  },
];
